import { create } from "zustand";

export type DamageSource = "fall" | "unknown" | string;

export const PLAYER_MAX_HEARTS = 4;
export const DAMAGE_FLASH_DURATION_MS = 300;

const getNow = () => (
  typeof performance !== "undefined" ? performance.now() : Date.now()
);

type PlayerHealthState = {
  maxHearts: number;
  currentHearts: number;
  lastDamageSource: DamageSource | null;
  lastDamageAt: number | null;
  damageSequence: number;
  isDamageFlashing: boolean;
  damageFlashTimeoutId: number | null;
  takeDamage: (source?: DamageSource) => void;
  restoreHearts: (amount: number) => void;
  resetHealth: () => void;
};

export const usePlayerHealth = create<PlayerHealthState>((set, get) => ({
  maxHearts: PLAYER_MAX_HEARTS,
  currentHearts: PLAYER_MAX_HEARTS,
  lastDamageSource: null,
  lastDamageAt: null,
  damageSequence: 0,
  isDamageFlashing: false,
  damageFlashTimeoutId: null,
  takeDamage: (source: DamageSource = "unknown") => {
    const { currentHearts, damageFlashTimeoutId, damageSequence } = get();

    if (damageFlashTimeoutId !== null && typeof window !== "undefined") {
      window.clearTimeout(damageFlashTimeoutId);
    }

    const nextHearts = Math.max(currentHearts - 1, 0);
    let timeoutId: number | null = null;

    if (typeof window !== "undefined") {
      timeoutId = window.setTimeout(() => {
        set({ isDamageFlashing: false, damageFlashTimeoutId: null });
      }, DAMAGE_FLASH_DURATION_MS);
    }

    set({
      currentHearts: nextHearts,
      lastDamageSource: source,
      lastDamageAt: getNow(),
      isDamageFlashing: true,
      damageFlashTimeoutId: timeoutId,
      damageSequence: damageSequence + 1,
    });
  },
  restoreHearts: (amount: number) => {
    if (!Number.isFinite(amount) || amount <= 0) return;

    const { currentHearts, maxHearts, damageFlashTimeoutId } = get();

    if (currentHearts >= maxHearts) return;

    if (damageFlashTimeoutId !== null && typeof window !== "undefined") {
      window.clearTimeout(damageFlashTimeoutId);
    }

    const nextHearts = Math.min(maxHearts, currentHearts + Math.floor(amount));

    set({
      currentHearts: nextHearts,
      lastDamageSource: null,
      lastDamageAt: null,
      isDamageFlashing: false,
      damageFlashTimeoutId: null,
    });
  },
  resetHealth: () => {
    const { damageFlashTimeoutId, maxHearts } = get();

    if (damageFlashTimeoutId !== null && typeof window !== "undefined") {
      window.clearTimeout(damageFlashTimeoutId);
    }

    set({
      currentHearts: maxHearts,
      lastDamageSource: null,
      lastDamageAt: null,
      isDamageFlashing: false,
      damageFlashTimeoutId: null,
    });
  },
}));
